// src/App.jsx
import React, { useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";

import LandingPage from "./pages/LandingPage";
import About from "./pages/About";
import Auth from "./pages/Auth";
import StoryApp from "./pages/StoryApp";
import Feedback from "./pages/Feedback";
import "./App.css";
import RegistrationForm from "./components/RegistrationForm";
import SubscriptionPage from "./pages/SubscriptionPage";
import LoginPage from "./pages/LoginPage";
import BillingPage from "./pages/BillingPage";
import CountrySelector from "./pages/CountrySelector";
import FreeDashboard from "./pages/FreeDashboard";
import PersonalDashboard from "./pages/PersonalDashboard";
import FamilyDashboard from "./pages/FamilyDashboard";
import SchoolDashboard from "./pages/SchoolDashboard";
import ManageStudents from "./pages/ManageStudents";
import ManageTeachers from "./pages/ManageTeachers";
import ClassroomInsights from "./pages/ClassroomInsights";
import SchoolMetrics from "./pages/SchoolMetrics";
import StoryAppUser from "./pages/StoryAppUser";
import Schools from "./pages/Schools";
import StudentDashboard from "./pages/StudentDashboard";
import TeacherDashboard from "./pages/TeacherDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import AdminLogin from "./pages/AdminLogin";
import Help from "./pages/Help";
import Contact from "./pages/Contact";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import BillingSuccessRedirect from "./pages/BillingSuccessRedirect";
import { AdminViewPersonal } from "./admin/AdminViewPersonal";
import { AdminViewFamily } from "./admin/AdminViewFamily";
import { AdminViewSchool } from "./admin/AdminViewSchool";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/TermsOfUse";
import CookiePolicy from "./pages/CookiePolicy";
import ChildSafety from "./pages/ChildSafety";
import Accessibility from "./pages/Accessibility";
import CookieBanner from "./components/CookieBanner";
import DataProtectionSchools from "./pages/DataProtectionSchools";
import AIForSchools from "./pages/AIForSchools";
import AiForSchoolsPage from "./pages/AiForSchoolsPage";
import ScrollToTop from "./components/ScrollToTop";



import { installAuthBootstrap } from "./bootstrap/authBootstrap";
import {
  RequireAuth,
  RequireAdmin,
  ForbidAdminOnUserRoutes,
  RequireSchoolContext,
} from "./routes/guards";

export default function App() {
  const navigate = useNavigate();

  // ✅ Re-enable the bootstrap that used to route you to the right dashboard.
  useEffect(() => {
    installAuthBootstrap(navigate);
  }, [navigate]);

  return (
    <>

      {/* Public */}
        <ScrollToTop />

      <Routes>
        
        <Route path="/" element={<LandingPage />} />
        <Route path="/about" element={<About />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/schools" element={<Schools />} />
        <Route path="/register" element={<RegistrationForm />} />
        <Route path="/subscriptions" element={<SubscriptionPage />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/country-selector" element={<CountrySelector />} />
        <Route path="/storyapp" element={<StoryApp />} />
        <Route path="/storyappuser" element={<StoryAppUser />} />
        <Route path="/help" element={<Help />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/billing/success" element={<BillingSuccessRedirect />} />
        <Route path="/data-protection" element={<DataProtectionSchools />} />
        <Route path="/ai-for-schools" element={<AIForSchools />} />
        <Route path="/ai-for-schools-page" element={<AiForSchoolsPage />} />

        {/* Admin "view as" routes */}
        <Route path="/admin/view-personal/:uid" element={<AdminViewPersonal />} />
        <Route path="/admin/view-family/:uid" element={<AdminViewFamily />} />
        <Route path="/admin/view-school/:uid" element={<AdminViewSchool />} />

        {/* Legal / policy pages */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfUse />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/child-safety" element={<ChildSafety />} />
        <Route path="/accessibility" element={<Accessibility />} />

        {/* Admin auth flow */}
        <Route path="/admin-login" element={<AdminLogin />} />

        {/* Admin area (guarded) */}
        <Route element={<RequireAdmin />}>
          <Route path="/admin-dashboard" element={<AdminDashboard />} />
        </Route>

        {/* User login should be hidden from admins */}
        <Route element={<ForbidAdminOnUserRoutes />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>

        {/* User-protected area */}
        <Route element={<RequireAuth />}>
          {/* No schoolId required */}
          <Route path="/free-dashboard" element={<FreeDashboard />} />
          <Route path="/student-dashboard" element={<StudentDashboard />} />
          <Route path="/teacher-dashboard" element={<TeacherDashboard />} />
          <Route path="/personal-dashboard" element={<PersonalDashboard />} />
          <Route path="/family-dashboard" element={<FamilyDashboard />} />

          {/* SchoolId required */}
          <Route element={<RequireSchoolContext />}>
            <Route path="/school-dashboard" element={<SchoolDashboard />} />
            <Route path="/manage-students" element={<ManageStudents />} />
            <Route path="/manage-teachers" element={<ManageTeachers />} />
            <Route path="/classroom-insights" element={<ClassroomInsights />} />
            <Route path="/school-metrics" element={<SchoolMetrics />} />
            <Route path="/billing" element={<BillingPage />} />
          </Route>
        </Route>

        {/* Fallbacks */}
        <Route path="/admin" element={<Navigate to="/admin-dashboard" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* ✅ Cookie banner rendered outside <Routes>, so it appears on all pages */}
      <CookieBanner />
    </>
  );
}
